// src/pages/CourseEdit.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  collection, doc, getDoc, getDocs, query, updateDoc, where,
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopy from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';

import {
  fetchDanceStyles,
  getDanceStyleGroups,
  getDanceStyleLabel,
  normalizeDanceStyleIds,
} from '../services/danceStylesService';

import {
  toBelgiumUTCISO,
  datetimeLocalToBelgiumUTCISO,
  formatForBelgiumDisplay,
  isoToBelgiumParts,
} from '../utils/dateTimeUtils';


/* ====================== Options UI ====================== */
const DIFFICULTY_OPTIONS = ['Débutant', 'Intermédiaire', 'Avancé', 'Tous niveaux'];
const AUDIENCE_OPTIONS = ['Enfants', 'Ados', 'Adultes', 'Seniors'];
const DURATION_OPTIONS = [
  { value: 30,  label: '30 minutes' },
  { value: 60,  label: '1 heure' },
  { value: 90,  label: '1h30' },
  { value: 120, label: '2 heures' },
  { value: 150, label: '2h30' },
  { value: 180, label: '3 heures' },
];

/* ====================== Helpers dates ====================== */
// "HH:mm" -> minutes depuis minuit
const hmToMinutes = (hm) => {
  if (!hm || !hm.includes(':')) return null;
  const [h, m] = hm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// "HH:mm" + minutes -> "HH:mm"
const addMinutesToHm = (hm, minutes) => {
  const start = hmToMinutes(hm);
  if (start === null) return '';
  const total = (start + (Number(minutes) || 0)) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// "YYYY-MM-DD" -> "Mardi" (midi pour éviter tout décalage de fuseau)
const ymdToDayLabel = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  const day = d.toLocaleDateString('fr-FR', { weekday: 'long' });
  return day.charAt(0).toUpperCase() + day.slice(1);
};

function CourseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [langTab, setLangTab] = useState('fr');

  const [courseData, setCourseData] = useState({
    title: '',
    description_i18n: { fr: '', en: '', nl: '' },
    description: '',
    difficultyLevels: [],
    audiences: [],
    danceStyles: [],
    maxStudents: 10,
    recurrence: 'none',
    teachers: [],
    dates: [],
    startDate: '',
    endDate: '',
    startTime: '18:00',
    endTime: '19:00',
    duration: 60,
    daysOfWeek: [],
    imageUrl: '',
    location: { name: '', address: '' },
    enrolledStudents: [],
    enrolledParents: [],
    isFull: false,
  });

  const [courseTeacherObjects, setCourseTeacherObjects] = useState([]);
  const [danceStyleOptions, setDanceStyleOptions] = useState([]);
  const [danceStylesLoading, setDanceStylesLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imageSource, setImageSource] = useState('url'); // 'url' ou 'upload'
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newDate, setNewDate] = useState('');
  const [timeMode, setTimeMode] = useState('duration');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  // Édition d'une date spécifique
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState({ ymd: '', startTime: '', endTime: '' });
  const [editError, setEditError] = useState('');

  // Statut de traduction
  const transStatus = useMemo(() => {
    const make = (l) => {
      const complete = !!courseData.description_i18n?.[l];
      return { complete, missing: complete ? 0 : 1, total: 1 };
    };
    return { en: make('en'), nl: make('nl') };
  }, [courseData.description_i18n]);

  /* -------- Charger le cours -------- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'dance_courses', id));
        if (!snap.exists()) { setError('Cours non trouvé'); return; }
        const data = snap.data();

        // Dates -> string ISO
        const dates = Array.isArray(data.dates)
          ? data.dates.map(d => ({
              ...d,
              date: d?.date?.toDate ? d.date.toDate().toISOString() : d.date,
            }))
          : [];

        // Profs -> objets + IDs
        const teacherArray = Array.isArray(data.teachers) ? data.teachers : [];
        const teacherIds = teacherArray.map(t => (typeof t === 'string' ? t : t?.id)).filter(Boolean);

        // Description i18n avec fallback
        const description_i18n = data.description_i18n || { 
          fr: data.description || '', 
          en: '', 
          nl: '' 
        };

        // Pour les cours créés avant l'enregistrement de startDate/endDate/daysOfWeek,
        // on déduit ces valeurs à partir des dates déjà générées afin que les champs
        // de configuration ne soient pas vides et que les dates restent modifiables.
        let derivedStartDate = data.startDate || '';
        let derivedEndDate = data.endDate || '';
        let derivedStartTime = data.startTime || '18:00';
        let derivedEndTime = data.endTime || '19:00';
        let derivedDaysOfWeek = Array.isArray(data.daysOfWeek) ? data.daysOfWeek : [];

        if (dates.length > 0) {
          const sorted = [...dates].sort((a, b) => new Date(a.date) - new Date(b.date));
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const firstParts = isoToBelgiumParts(first.date);

          if (!derivedStartDate) derivedStartDate = firstParts.ymd;
          if (!derivedEndDate) derivedEndDate = isoToBelgiumParts(last.date).ymd;
          if (!data.startTime) derivedStartTime = first.startTime || firstParts.hm || derivedStartTime;
          if (!data.endTime) derivedEndTime = first.endTime || derivedEndTime;
          if (derivedDaysOfWeek.length === 0) {
            derivedDaysOfWeek = [...new Set(dates.map(d => d.day).filter(Boolean))];
          }
        }

        setCourseTeacherObjects(teacherArray);
        setCourseData(prev => ({
          ...prev,
          ...data,
          description_i18n,
          teachers: teacherIds,
          dates,
          location: data.location || { name: '', address: '' },
          startDate: derivedStartDate,
          endDate: derivedEndDate,
          startTime: derivedStartTime,
          endTime: derivedEndTime,
          daysOfWeek: derivedDaysOfWeek,
          difficultyLevels: Array.isArray(data.difficultyLevels) ? data.difficultyLevels : [],
          audiences: Array.isArray(data.audiences) ? data.audiences : [],
          danceStyles: normalizeDanceStyleIds(data.danceStyles),
        }));

        // Pré-remplir l'URL de l'image
        setImageUrlInput(data.imageUrl || '');
        
        setLoading(false);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement du cours');
        setLoading(false);
      }
    })();
  }, [id]);

  /* -------- Chargement des styles de danse -------- */
  useEffect(() => {
    (async () => {
      try {
        const styles = await fetchDanceStyles();
        setDanceStyleOptions(styles);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des styles de danse');
      } finally {
        setDanceStylesLoading(false);
      }
    })();
  }, []);

  const danceStyleGroups = useMemo(
    () => getDanceStyleGroups(danceStyleOptions),
    [danceStyleOptions]
  );

  const danceStyleLabel = (styleId) =>
    getDanceStyleLabel(danceStyleOptions, styleId);

  const handleDanceStylesChange = (event) => {
    const selected = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    const normalized = normalizeDanceStyleIds(selected);
    const withParents = [...normalized];

    normalized.forEach((styleId) => {
      const style = danceStyleOptions.find((item) => item.id === styleId);
      if (style?.parentId && !withParents.includes(style.parentId)) {
        withParents.push(style.parentId);
      }
    });

    setCourseData((previous) => ({
      ...previous,
      danceStyles: withParents,
    }));
  };

  /* -------- Chargement des professeurs -------- */
  useEffect(() => {
    (async () => {
      try {
        const usersQ = query(collection(db, 'users'), where('role', '==', 'Professeur'));
        const usersSnap = await getDocs(usersQ);

        const list = await Promise.all(
          usersSnap.docs.map(async (userDoc) => {
            try {
              const tDoc = await getDoc(doc(db, 'teachers', userDoc.id));
              return {
                id: userDoc.id,
                ...userDoc.data(),
                ...(tDoc.exists() ? tDoc.data() : {}),
              };
            } catch {
              return { id: userDoc.id, ...userDoc.data() };
            }
          })
        );

        setTeachers(list);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des professeurs');
      }
    })();
  }, []);

  /* -------- Union affichable des professeurs -------- */
  const allTeachers = useMemo(() => {
    const map = new Map(teachers.map(t => [t.id, t]));
    for (const t of courseTeacherObjects) {
      const id = typeof t === 'string' ? t : t?.id;
      if (id && !map.has(id)) {
        map.set(id, {
          id,
          firstName: t?.firstName || '',
          lastName:  t?.lastName  || '',
          pseudo:    t?.pseudo    || '',
        });
      }
    }
    return Array.from(map.values());
  }, [teachers, courseTeacherObjects]);

  /* -------- Handlers -------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseData(prev => ({ ...prev, [name]: value }));
  };

  const handleLangChange = (field, value) => {
    setCourseData(prev => {
      const next = { ...prev };
      next[`${field}_i18n`] = { ...prev[`${field}_i18n`], [langTab]: value };
      if (langTab === 'fr') {
        next[field] = value;
      }
      return next;
    });
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setCourseData(prev => ({ ...prev, location: { ...prev.location, [name]: value } }));
  };

  const handleTeacherChange = (event) => {
    const { value } = event.target;
    setCourseData(prev => ({ ...prev, teachers: typeof value === 'string' ? value.split(',') : value }));
  };

  const handleDayChange = (day) => {
    setCourseData(prev => {
      const days = [...prev.daysOfWeek];
      const i = days.indexOf(day);
      if (i > -1) days.splice(i, 1); else days.push(day);
      return { ...prev, daysOfWeek: days };
    });
  };

  // Fonctions de copie FR → EN/NL
  const copyFromFRToLang = (lang) => {
    setCourseData(prev => ({
      ...prev,
      description_i18n: { ...prev.description_i18n, [lang]: prev.description_i18n.fr || prev.description || '' }
    }));
  };

  const copyFRToAllEmpty = () => {
    setCourseData(prev => ({
      ...prev,
      description_i18n: {
        fr: prev.description_i18n.fr || prev.description || '',
        en: prev.description_i18n.en || prev.description_i18n.fr || prev.description || '',
        nl: prev.description_i18n.nl || prev.description_i18n.fr || prev.description || ''
      }
    }));
  };

  // Calcul automatique endTime en mode durée
  useEffect(() => {
    if (timeMode !== 'duration' || !courseData.startTime) return;
    const [h, m] = courseData.startTime.split(':').map(Number);
    const start = new Date(); start.setHours(h, m, 0, 0);
    const end = new Date(start.getTime() + (Number(courseData.duration) || 0) * 60000);
    const hh = String(end.getHours()).padStart(2, '0');
    const mm = String(end.getMinutes()).padStart(2, '0');
    setCourseData(prev => ({ ...prev, endTime: `${hh}:${mm}` }));
  }, [courseData.startTime, courseData.duration, timeMode]);

  /* -------- Gestion des dates -------- */
  const addManualDate = () => {
    if (!newDate) return;
    const dateObj = new Date(newDate);
    const dayOfWeek = dateObj.toLocaleDateString('fr-FR', { weekday: 'long' });
    const formattedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
    // Heure de début = heure saisie dans le champ datetime-local
    const startHm = newDate.slice(11, 16) || courseData.startTime;
    setCourseData(prev => ({
      ...prev,
      dates: [
        ...prev.dates,
        {
          date: datetimeLocalToBelgiumUTCISO(newDate),
          day: formattedDay,
          startTime: startHm,
          endTime: addMinutesToHm(startHm, prev.duration),
          duration: prev.duration,
        },
      ].sort((a, b) => new Date(a.date) - new Date(b.date)),
    }));
    setNewDate('');
  };

  const removeManualDate = (idx) => {
    cancelEditDate();
    setCourseData(prev => {
      const copy = [...prev.dates];
      copy.splice(idx, 1);
      return { ...prev, dates: copy };
    });
  };

  /* -------- Modifier UNE date spécifique -------- */
  const startEditDate = (idx) => {
    const d = courseData.dates[idx];
    if (!d) return;
    const parts = isoToBelgiumParts(d.date);
    const startTime = d.startTime || parts.hm || courseData.startTime;
    const endTime = d.endTime || addMinutesToHm(startTime, d.duration || courseData.duration);
    setEditDraft({ ymd: parts.ymd, startTime, endTime });
    setEditError('');
    setEditingIdx(idx);
  };

  const cancelEditDate = () => {
    setEditingIdx(null);
    setEditDraft({ ymd: '', startTime: '', endTime: '' });
    setEditError('');
  };

  const saveEditDate = () => {
    const { ymd, startTime, endTime } = editDraft;
    if (!ymd || !startTime || !endTime) {
      setEditError('Date, heure de début et heure de fin sont obligatoires');
      return;
    }
    const startMin = hmToMinutes(startTime);
    const endMin = hmToMinutes(endTime);
    if (startMin === null || endMin === null || endMin <= startMin) {
      setEditError("L'heure de fin doit être après l'heure de début");
      return;
    }

    setCourseData(prev => {
      const dates = [...prev.dates];
      dates[editingIdx] = {
        ...dates[editingIdx],
        date: toBelgiumUTCISO(ymd, startTime),
        day: ymdToDayLabel(ymd),
        startTime,
        endTime,
        duration: endMin - startMin,
        isCustom: true, // horaire modifié manuellement
      };
      dates.sort((a, b) => new Date(a.date) - new Date(b.date));
      return { ...prev, dates };
    });
    cancelEditDate();
  };

  // Une date diffère-t-elle de l'horaire général du cours ?
  const isDifferentFromDefault = (d) =>
    !!d.isCustom ||
    (d.startTime && d.startTime !== courseData.startTime) ||
    (d.endTime && d.endTime !== courseData.endTime);

  const generateRecurringDates = () => {
    const dates = [];
    const start = new Date(courseData.startDate);
    const end = new Date(courseData.endDate);
    if (!(courseData.startDate && courseData.endDate) || start > end) {
      setError('Veuillez saisir une période valide'); return;
    }
    if (!courseData.startTime) {
      setError("Veuillez définir l'heure de début"); return;
    }

    // Protection : régénérer écrase les horaires modifiés à la main
    const customCount = courseData.dates.filter(d => d.isCustom).length;
    if (customCount > 0 && !window.confirm(
      `${customCount} date(s) ont un horaire modifié manuellement. ` +
      'Régénérer les dates va les remplacer. Continuer ?'
    )) {
      return;
    }

    const wantedDays = courseData.daysOfWeek;

    const cur = new Date(start);
    while (cur <= end) {
      const day = cur.toLocaleDateString('fr-FR', { weekday: 'long' });
      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
      if (wantedDays.includes(dayLabel)) {
        const ymd = cur.toISOString().slice(0, 10);
        dates.push({
          date: toBelgiumUTCISO(ymd, courseData.startTime),
          day: dayLabel,
          startTime: courseData.startTime,
          endTime: courseData.endTime,
          duration: courseData.duration,
        });
      }
      cur.setDate(cur.getDate() + 1);
    }
    cancelEditDate();
    setCourseData(prev => ({ ...prev, dates }));
  };

  /* -------- Upload image CORRIGÉ -------- */
  const handleImageUpload = async () => {
    // Si source = URL, retourner l'URL saisie
    if (imageSource === 'url') {
      return imageUrlInput.trim();
    }

    // Si source = upload mais pas de fichier, garder l'image existante
    if (!imageFile) {
      return courseData.imageUrl;
    }

    try {
      // Supprimer l'ancienne image si elle existe et est dans Firebase Storage
      if (courseData.imageUrl && courseData.imageUrl.includes('firebasestorage.googleapis.com')) {
        try {
          const oldImageRef = ref(storage, courseData.imageUrl);
          await deleteObject(oldImageRef);
        } catch (deleteError) {
          console.warn('Impossible de supprimer l\'ancienne image:', deleteError);
        }
      }

      const safeName = imageFile.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');

      const storageRef = ref(storage, `course-images/${Date.now()}_${safeName}`);
      const snapshot = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (e) {
      console.error('Erreur upload image:', e);
      setError("Erreur lors du téléchargement de l'image. L'ancienne image sera conservée.");
      return courseData.imageUrl;
    }
  };

  /* -------- Submit CORRIGÉ -------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); 
    setSuccess('');

    if (editingIdx !== null) {
      setError('Une date est en cours de modification : validez-la ou annulez avant d\'enregistrer.');
      return;
    }
    if (!courseData.title || !courseData.description) {
      setError('Veuillez remplir tous les champs obligatoires'); 
      return;
    }
    if (courseData.dates.length === 0 && courseData.recurrence === 'none' && !courseData.startDate) {
      setError('Veuillez ajouter au moins une date'); 
      return;
    }
    if (!courseData.location.name || !courseData.location.address) {
      setError("Veuillez renseigner le nom et l'adresse du lieu"); 
      return;
    }

    try {
      // Gestion de l'image
      const finalImageUrl = await handleImageUpload();

      // Gestion des dates
      let finalDates = [...courseData.dates];
      if (courseData.recurrence === 'none' && courseData.startDate) {
        const dayOfWeek = new Date(courseData.startDate).toLocaleDateString('fr-FR', { weekday: 'long' });
        const formattedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
        finalDates = [{
          date: toBelgiumUTCISO(courseData.startDate, courseData.startTime),
          day: formattedDay,
          startTime: courseData.startTime,
          endTime: courseData.endTime,
          duration: courseData.duration,
        }];
      }

      // Formatage des professeurs
      const teacherObjects = courseData.teachers.map(id => {
        const t = allTeachers.find(x => x.id === id) || {};
        const fullName = `${t.firstName || ''} ${t.lastName || ''}`.trim();
        return {
          id,
          firstName: t.firstName || '',
          lastName: t.lastName || '',
          pseudo: t.pseudo || fullName,
        };
      });

      // Mise à jour du cours
      await updateDoc(doc(db, 'dance_courses', id), {
        title: courseData.title,
        description: courseData.description,
        description_i18n: courseData.description_i18n,
        difficultyLevels: courseData.difficultyLevels,
        audiences: courseData.audiences,
        danceStyles: normalizeDanceStyleIds(courseData.danceStyles),
        maxStudents: Number(courseData.maxStudents) || 0,
        recurrence: courseData.recurrence,
        duration: courseData.duration,
        startDate: courseData.startDate || '',
        endDate: courseData.endDate || '',
        startTime: courseData.startTime || '',
        endTime: courseData.endTime || '',
        daysOfWeek: courseData.daysOfWeek || [],
        teachers: teacherObjects,
        dates: finalDates,
        imageUrl: finalImageUrl,
        location: { 
          name: courseData.location.name, 
          address: courseData.location.address 
        },
        enrolledStudents: courseData.enrolledStudents || [],
        enrolledParents: courseData.enrolledParents || [],
        isFull: !!courseData.isFull,
        updatedAt: new Date().toISOString(),
      });

      setSuccess('Cours mis à jour avec succès !');
      setTimeout(() => navigate('/courses'), 1200);
    } catch (err) {
      console.error('Erreur mise à jour cours:', err);
      setError('Erreur lors de la mise à jour du cours: ' + err.message);
    }
  };

  const currentDescription = courseData.description_i18n[langTab] || '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Chargement du cours...</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <IconButton onClick={() => navigate('/courses')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>
          Modifier le cours
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <form onSubmit={handleSubmit} noValidate>
        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Informations du cours
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Titre du cours *" name="title" 
                value={courseData.title} onChange={handleChange} required />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Tabs value={langTab} onChange={(e, v) => setLangTab(v)} sx={{ mb: 1 }}>
                  <Tab value="fr" label="Français" />
                  <Tab value="en" label="English" />
                  <Tab value="nl" label="Nederlands" />
                </Tabs>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {langTab !== 'fr' && (
                    <Tooltip title={`Copier depuis FR vers ${langTab.toUpperCase()}`}>
                      <Button variant="outlined" size="small" startIcon={<ContentCopy />}
                        onClick={() => copyFromFRToLang(langTab)}>
                        Copier depuis FR
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Remplir EN & NL avec FR (seulement les champs vides)">
                    <Button variant="text" size="small" startIcon={<ContentCopy />}
                      onClick={copyFRToAllEmpty}>
                      Remplir vides EN/NL
                    </Button>
                  </Tooltip>

                  <Chip size="small" label={`EN ${transStatus.en.total - transStatus.en.missing}/${transStatus.en.total}`}
                    color={transStatus.en.complete ? 'success' : 'default'} />
                  <Chip size="small" label={`NL ${transStatus.nl.total - transStatus.nl.missing}/${transStatus.nl.total}`}
                    color={transStatus.nl.complete ? 'success' : 'default'} />
                </Box>
              </Box>

              <TextField 
                fullWidth 
                label={`Description (${langTab.toUpperCase()}) ${langTab === 'fr' ? '*' : ''}`} 
                multiline 
                rows={3} 
                value={currentDescription}
                onChange={(e) => handleLangChange('description', e.target.value)}
                required={langTab === 'fr'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Niveau(x) *</InputLabel>
                <Select
                  multiple
                  label="Niveau(x) *"
                  value={courseData.difficultyLevels}
                  onChange={(e) => setCourseData(prev => ({ ...prev, difficultyLevels: e.target.value }))}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {DIFFICULTY_OPTIONS.map(level => (
                    <MenuItem key={level} value={level}>
                      <Checkbox checked={courseData.difficultyLevels.includes(level)} />
                      <ListItemText primary={level} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Public *</InputLabel>
                <Select
                  multiple
                  label="Public *"
                  value={courseData.audiences}
                  onChange={(e) => setCourseData(prev => ({ ...prev, audiences: e.target.value }))}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {AUDIENCE_OPTIONS.map(a => (
                    <MenuItem key={a} value={a}>
                      <Checkbox checked={courseData.audiences.includes(a)} />
                      <ListItemText primary={a} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={danceStylesLoading}>
                <InputLabel>Style(s) de danse</InputLabel>
                <Select
                  multiple
                  label="Style(s) de danse"
                  value={courseData.danceStyles || []}
                  onChange={handleDanceStylesChange}
                  renderValue={(selected) => selected.map(danceStyleLabel).join(', ')}
                >
                  {danceStyleGroups.map((category) => [
                    <MenuItem key={category.id} value={category.id} sx={{ fontWeight: 700 }}>
                      <Checkbox checked={(courseData.danceStyles || []).includes(category.id)} />
                      <ListItemText primary={category.label} secondary="Catégorie" />
                    </MenuItem>,
                    ...category.children.map((child) => (
                      <MenuItem key={child.id} value={child.id} sx={{ pl: 4 }}>
                        <Checkbox checked={(courseData.danceStyles || []).includes(child.id)} />
                        <ListItemText primary={child.label} secondary={category.label} />
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {danceStylesLoading
                  ? 'Chargement des styles...'
                  : 'La catégorie parente est ajoutée automatiquement lorsque vous choisissez une sous-catégorie.'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" inputProps={{ min: 1 }}
                label="Nombre max d'étudiants"
                name="maxStudents"
                value={courseData.maxStudents}
                onChange={handleChange}
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={!!courseData.isFull}
                    onChange={(e) => setCourseData(prev => ({ ...prev, isFull: e.target.checked }))}
                  />
                }
                label="🔒 Cours complet (bloque les inscriptions, affiche la liste d'attente)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="teachers-select-label">Professeur(s)</InputLabel>
                <Select
                  multiple
                  labelId="teachers-select-label"
                  input={<OutlinedInput label="Professeur(s)" />}
                  name="teachers"
                  value={courseData.teachers}
                  onChange={handleTeacherChange}
                  renderValue={(selected) =>
                    selected.map(id => {
                      const t = allTeachers.find(tt => tt.id === id);
                      return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() : id;
                    }).join(', ')
                  }
                >
                  {allTeachers.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={courseData.teachers.includes(t.id)} />
                      <ListItemText primary={`${t.firstName || ''} ${t.lastName || ''}`.trim() || t.pseudo || t.id} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Lieu du cours
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du lieu *"
                name="name"
                value={courseData.location.name}
                onChange={handleLocationChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Adresse *"
                name="address"
                value={courseData.location.address}
                onChange={handleLocationChange}
                required
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Configuration des dates
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Récurrence</InputLabel>
                <Select name="recurrence" label="Récurrence" value={courseData.recurrence} onChange={handleChange}>
                  <MenuItem value="none">Date unique</MenuItem>
                  <MenuItem value="weekly">Hebdomadaire</MenuItem>
                  <MenuItem value="monthly">Mensuel</MenuItem>
                  <MenuItem value="multiple">Dates multiples</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {courseData.recurrence === 'none' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date du cours *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="time" label="Heure de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startTime} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startTime: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select fullWidth label="Durée *"
                    value={courseData.duration}
                    onChange={(e) => setCourseData(prev => ({ ...prev, duration: parseInt(e.target.value, 10) }))}
                    required
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {['weekly', 'monthly'].includes(courseData.recurrence) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Jours de la semaine *</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'].map(day => (
                      <Button key={day} variant={courseData.daysOfWeek.includes(day) ? 'contained' : 'outlined'} 
                        size="small" onClick={() => handleDayChange(day)}>
                        {day}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date de fin *" InputLabelProps={{ shrink: true }} 
                    value={courseData.endDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, endDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="time" label="Heure de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startTime} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startTime: e.target.value }))} 
                    required 
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} sx={{ mt: 1 }}>
                    <Button variant={timeMode === 'duration' ? 'contained' : 'outlined'} 
                      onClick={() => setTimeMode('duration')}>Par durée</Button>
                    <Button variant={timeMode === 'range' ? 'contained' : 'outlined'} 
                      onClick={() => setTimeMode('range')}>Par plage</Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  {timeMode === 'duration' ? (
                    <TextField
                      select fullWidth label="Durée *"
                      value={courseData.duration}
                      onChange={(e) => setCourseData(prev => ({ ...prev, duration: parseInt(e.target.value, 10) }))}
                      required
                    >
                      {DURATION_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField fullWidth type="time" label="Heure de fin *" InputLabelProps={{ shrink: true }} 
                      value={courseData.endTime} 
                      onChange={(e) => setCourseData(prev => ({ ...prev, endTime: e.target.value }))} 
                      required 
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Button variant="contained" startIcon={<EventIcon />} 
                    onClick={generateRecurringDates} 
                    disabled={!courseData.startDate || !courseData.endDate || courseData.daysOfWeek.length === 0}>
                    Générer les dates
                  </Button>
                </Grid>
              </>
            )}

            {courseData.recurrence === 'multiple' && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Ajoutez chaque date manuellement ci-dessous.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Dates programmées {courseData.dates.length > 0 ? `(${courseData.dates.length})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Cliquez sur ✏️ pour changer le jour ou l'horaire d'une seule séance, sans toucher aux autres.
              </Typography>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <TextField fullWidth type="datetime-local" label="Ajouter une date" 
                  value={newDate} 
                  onChange={(e) => setNewDate(e.target.value)} 
                  InputLabelProps={{ shrink: true }} 
                />
                <Button variant="contained" startIcon={<AddIcon />} 
                  onClick={addManualDate} disabled={!newDate}>
                  Ajouter
                </Button>
              </Box>
              {courseData.dates.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 320, overflow: 'auto' }}>
                  {courseData.dates.map((d, i) => (
                    <Box
                      key={`${d.date}-${i}`}
                      sx={{
                        py: 1, px: 1, borderBottom: '1px solid #eee',
                        bgcolor: editingIdx === i ? '#f0f7ff' : 'transparent',
                        borderRadius: 1,
                      }}
                    >
                      {editingIdx === i ? (
                        <>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <TextField
                              size="small" type="date" label="Date"
                              InputLabelProps={{ shrink: true }}
                              value={editDraft.ymd}
                              onChange={(e) => setEditDraft(prev => ({ ...prev, ymd: e.target.value }))}
                            />
                            <TextField
                              size="small" type="time" label="Début"
                              InputLabelProps={{ shrink: true }}
                              value={editDraft.startTime}
                              onChange={(e) => setEditDraft(prev => ({ ...prev, startTime: e.target.value }))}
                            />
                            <TextField
                              size="small" type="time" label="Fin"
                              InputLabelProps={{ shrink: true }}
                              value={editDraft.endTime}
                              onChange={(e) => setEditDraft(prev => ({ ...prev, endTime: e.target.value }))}
                            />
                            <Tooltip title="Valider">
                              <IconButton size="small" color="primary" onClick={saveEditDate}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Annuler">
                              <IconButton size="small" onClick={cancelEditDate}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {editError && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {editError}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2">
                              {formatForBelgiumDisplay(d.date)}
                              {d.endTime ? ` → ${d.endTime}` : ''}
                            </Typography>
                            {isDifferentFromDefault(d) && (
                              <Chip size="small" color="info" variant="outlined" label="Horaire spécifique" />
                            )}
                          </Box>
                          <Box>
                            <Tooltip title="Modifier cette date uniquement">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => startEditDate(i)}
                                  disabled={editingIdx !== null && editingIdx !== i}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Supprimer cette date">
                              <IconButton size="small" onClick={() => removeManualDate(i)} color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Paper>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Aucune date programmée pour ce cours pour le moment.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
            Image du cours
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={imageSource}
              onChange={(e) => setImageSource(e.target.value)}
            >
              <FormControlLabel value="url" control={<Radio />} label="URL d'image" />
              <FormControlLabel value="upload" control={<Radio />} label="Télécharger une image" />
            </RadioGroup>
          </FormControl>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              {imageSource === 'url' ? (
                <TextField
                  fullWidth
                  label="URL de l'image"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: <LinkIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              ) : (
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}>
                  Télécharger une image
                  <input 
                    hidden 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => setImageFile(e.target.files?.[0] ?? null)} 
                  />
                </Button>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {(imageSource === 'upload' && imageFile) ? (
                <img src={URL.createObjectURL(imageFile)} alt="Aperçu" 
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd' }} />
              ) : courseData.imageUrl ? (
                <img src={courseData.imageUrl} alt="Aperçu du cours" 
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd' }} />
              ) : (
                <Box sx={{ bgcolor: '#f5f5f5', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, border: '1px dashed #ddd' }}>
                  <Typography variant="body2" color="textSecondary">Aperçu de l'image</Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} 
            sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 'bold', boxShadow: 2 }}>
            Mettre à jour le cours
          </Button>
        </Box>
      </form>
    </div>
  );
}

export default CourseEdit;